import React, { useEffect, useMemo, useRef, useState } from 'react';
import { message } from '../utils/antdMessage';
import BasemapLayerControl from '../components/BasemapLayerControl';
import {
  switchDashboardBasemap,
  toggleDashboardOverlay
} from '../utils/dashboard';
import {
  MAP_PROVIDER_CATALOG,
  getEnabledProviders,
  applyBasemap,
  applyOverlayLayers,
  canUseProvider,
  getProviderHint,
  writeLastBasemapChoice,
  writeLastOverlayChoice,
  getStyleModeFromBasemapId,
  resolveBasemapIdForStyleMode,
  getStyleModeAvailability,
  getBasemapFamilyLabel,
  syncOverlaysForStyleMode,
  resolveFirstAvailableStyleMode,
  attachUnreachableRasterFallback,
  pickReachableSatelliteBasemap,
  findOverlayDatumMismatches
} from '../utils/mapProviderSettings';
import { setActiveBasemapIdForCoord } from '../utils/dashboard/basemapCoord';
import { rememberEarthSpinLng } from '../utils/dashboard/mapInitHelpers';
import {
  enableMapTerrain3d,
  disableMapTerrain3d,
  setTerrain3dWanted,
  defaultTerrain3dForStyle
} from '../utils/dashboard/mapTerrainHelpers';

/**
 * 指挥页底图 / 叠加层切换与控件渲染
 */
export default function useDashboardBasemapControls({
  getMap,
  mapProviderSettings,
  mapProviderSettingsRef,
  currentBasemapId,
  setCurrentBasemapId,
  currentBasemapIdRef,
  activeOverlayIds,
  setActiveOverlayIds,
  activeOverlayIdsRef,
  showBasemapPanel,
  setShowBasemapPanel,
  onBasemapUserPick,
  /** 底图样式就绪后重绘队员/轨迹/救援（GCJ 纠偏） */
  refreshMapOverlaysAfterBasemap
}) {
  const [currentMapStyleId, setCurrentMapStyleId] = useState(() => {
    const inferred = getStyleModeFromBasemapId(currentBasemapId);
    const pref = mapProviderSettings?.defaultStyleMode;
    let mode = inferred;
    if (pref === 'hybrid' && (inferred === 'hybrid' || inferred === 'satellite')) mode = 'hybrid';
    else if (pref && pref === inferred) mode = pref;
    const terrainOn = defaultTerrain3dForStyle(mode);
    if (typeof window !== 'undefined') window.__zhxtHybridStyleOn = mode === 'hybrid';
    setTerrain3dWanted(terrainOn);
    return mode;
  });
  const [terrain3dOn, setTerrain3dOn] = useState(() => (
    typeof window !== 'undefined' ? Boolean(window.__zhxtTerrain3dWanted) : false
  ));
  const googleTileWatchRef = useRef(null);
  const datumWarnKeyRef = useRef('');

  /** 叠加层与底图坐标系不一致（如天地图底图 + 高德注记）时提示一次：注记会整体偏几百米 */
  const warnOverlayDatumMismatch = (basemapId, overlayIds) => {
    try {
      const bad = findOverlayDatumMismatches(basemapId, overlayIds, mapProviderSettingsRef.current);
      const key = bad.length ? `${basemapId}|${bad.map((x) => x.id).join(',')}` : '';
      if (key && key !== datumWarnKeyRef.current) {
        const names = bad.map((x) => `${x.label}（${x.datum}）`).join('、');
        message.warning(`${names}与当前底图坐标系（${bad[0].basemapDatum}）不一致，叠加后会偏几百米，建议选同一图商的注记。`);
      }
      datumWarnKeyRef.current = key;
    } catch (_) {
      /* 提示失败不影响切换 */
    }
  };

  useEffect(() => () => {
    googleTileWatchRef.current?.();
    googleTileWatchRef.current = null;
  }, []);

  // 图层面板切换底图时，同步左上角高亮 + 底图坐标系（WGS84/GCJ02）
  useEffect(() => {
    const inferred = getStyleModeFromBasemapId(currentBasemapId);
    setCurrentMapStyleId((prev) => {
      if (prev === 'hybrid' && (inferred === 'hybrid' || inferred === 'satellite')) {
        return 'hybrid';
      }
      return inferred;
    });
    setActiveBasemapIdForCoord(currentBasemapId, getMap?.());
    // getMap 读 mapRef，无需列入依赖
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBasemapId]);

  const styleModeAvailability = useMemo(
    () => getStyleModeAvailability(currentBasemapId, mapProviderSettings),
    [currentBasemapId, mapProviderSettings]
  );

  const applyTerrainPresentation = (wanted) => {
    const map = getMap?.();
    const on = wanted == null
      ? (typeof window !== 'undefined' ? Boolean(window.__zhxtTerrain3dWanted) : terrain3dOn)
      : Boolean(wanted);
    if (on) enableMapTerrain3d(map);
    else disableMapTerrain3d(map);
  };

  const applyStyleModePresentation = (styleMode) => {
    if (typeof window !== 'undefined') {
      window.__zhxtHybridStyleOn = styleMode === 'hybrid';
    }
    applyTerrainPresentation();
  };

  const resetTerrainForStyleMode = (styleMode) => {
    const next = defaultTerrain3dForStyle(styleMode);
    setTerrain3dOn(next);
    setTerrain3dWanted(next);
    return next;
  };

  const toggleTerrain3d = () => {
    const next = !terrain3dOn;
    setTerrain3dOn(next);
    setTerrain3dWanted(next);
    applyTerrainPresentation(next);
  };

  const switchBasemap = (basemapId, options = {}) => {
    const map = getMap?.();
    // 必须在改基准之前记下旧底图，否则 applyBasemap 会认为坐标系未变、跳过 flyTo 居中
    const prevBasemapId = currentBasemapIdRef.current;
    const overlayIds = options.overlayIds || activeOverlayIdsRef.current;
    const keepStyleMode = options.styleMode;
    const result = switchDashboardBasemap({
      map,
      basemapId,
      settings: mapProviderSettingsRef.current,
      activeOverlayIds: overlayIds,
      catalog: MAP_PROVIDER_CATALOG,
      applyBasemap,
      canUseProvider,
      getProviderHint,
      writeLastBasemapChoice,
      prevBasemapId,
      onBasemapSettled: (id, meta) => {
        setActiveBasemapIdForCoord(id, getMap?.());
        // 用纠偏后的中心更新自转记忆，避免把高德 GCJ 经度带进 Esri/Mapbox
        if (meta?.center && Number.isFinite(Number(meta.center[0]))) {
          rememberEarthSpinLng(meta.center[0]);
        }
        const mode = keepStyleMode || currentMapStyleId;
        applyStyleModePresentation(mode);
        return refreshMapOverlaysAfterBasemap?.(id, meta);
      }
    });
    if (!result.ok) {
      message.warning(result.warning);
      return;
    }
    try {
      if (map) rememberEarthSpinLng(map.getCenter()?.lng);
    } catch (_) {
      /* ignore */
    }
    onBasemapUserPick?.(result.basemapId);
    setCurrentBasemapId(result.basemapId);
    warnOverlayDatumMismatch(result.basemapId, overlayIds);
    if (keepStyleMode) setCurrentMapStyleId(keepStyleMode);
    else setCurrentMapStyleId(result.styleId);
    const familyLabel = getBasemapFamilyLabel(result.basemapId);
    if (options.fromFallback) {
      /* 失败回退不再重复成功提示 */
    } else if (result.provider?.provider === 'google') {
      message.warning('已切换谷歌影像。国内网络通常访问不了谷歌瓦片，若地图空白会自动改用其它卫星图。');
    } else if (result.provider?.provider === 'gaode') {
      message.success(`已切换为${familyLabel}（已自动纠偏 GCJ-02，与队员 GPS/WGS84 对齐）`);
    } else if (result.provider?.provider === 'esri') {
      message.success(`已切换为${familyLabel}（WGS84，无需国测局纠偏）`);
    } else {
      message.success(`已切换为${familyLabel}`);
    }

    googleTileWatchRef.current?.();
    googleTileWatchRef.current = null;
    if (result.provider?.provider === 'google' && !options.fromFallback && map) {
      googleTileWatchRef.current = attachUnreachableRasterFallback(map, {
        onFallback: () => {
          const fb = pickReachableSatelliteBasemap(
            mapProviderSettingsRef.current,
            result.basemapId
          );
          if (!fb) {
            message.error('谷歌影像加载失败：国内访问不了 mt.google.com。请改选高德、天地图、Esri 或 Mapbox 卫星。');
            return;
          }
          const fbLabel = getBasemapFamilyLabel(fb);
          message.warning(`谷歌影像加载失败（国内网络打不开谷歌瓦片）。已改用${fbLabel}。`);
          resetTerrainForStyleMode('satellite');
          switchBasemap(fb, { styleMode: 'satellite', fromFallback: true });
        }
      });
    }
  };

  const toggleOverlay = (overlayId) => {
    const result = toggleDashboardOverlay({
      overlayId,
      currentOverlayIds: activeOverlayIdsRef.current,
      currentBasemapId: currentBasemapIdRef.current,
      settings: mapProviderSettingsRef.current,
      catalog: MAP_PROVIDER_CATALOG,
      map: getMap?.(),
      applyBasemap,
      applyOverlayLayers,
      canUseProvider,
      getProviderHint,
      writeLastOverlayChoice,
      onBasemapSettled: (id, meta) => {
        setActiveBasemapIdForCoord(id, getMap?.());
        return refreshMapOverlaysAfterBasemap?.(id, meta);
      }
    });
    if (result.info) {
      message.info(result.info);
      return;
    }
    if (!result.ok) {
      message.warning(result.warning);
      return;
    }
    setActiveOverlayIds(result.nextIds);
    warnOverlayDatumMismatch(currentBasemapIdRef.current, result.nextIds);
  };

  /** 左上角：二维 / 卫星 / 混合（及高程、地形） */
  const switchMapStyle = (styleMode) => {
    const settings = mapProviderSettingsRef.current;
    const currentId = currentBasemapIdRef.current;
    const resolved = resolveBasemapIdForStyleMode(currentId, styleMode, settings);
    if (!resolved.basemapId) {
      message.warning(resolved.hint || '当前图源不支持该样式');
      return;
    }
    if (styleMode !== currentMapStyleId) {
      resetTerrainForStyleMode(styleMode);
    }
    const nextOverlays = syncOverlaysForStyleMode(
      resolved.basemapId,
      styleMode,
      activeOverlayIdsRef.current,
      settings
    );
    activeOverlayIdsRef.current = nextOverlays;
    setActiveOverlayIds(nextOverlays);
    writeLastOverlayChoice(nextOverlays);
    setCurrentMapStyleId(styleMode);
    if (resolved.basemapId === currentId) {
      const map = getMap?.();
      applyBasemap?.(map, currentId, nextOverlays, settings, {
        prevBasemapId: currentId,
        onSettled: (id, meta) => {
          setActiveBasemapIdForCoord(id, getMap?.());
          applyStyleModePresentation(styleMode);
          return refreshMapOverlaysAfterBasemap?.(id, meta);
        }
      });
      applyStyleModePresentation(styleMode);
      return;
    }
    switchBasemap(resolved.basemapId, { overlayIds: nextOverlays, styleMode });
  };

  const switchBasemapKeepingMode = (seedId) => {
    const settings = mapProviderSettingsRef.current;
    const prevMode = currentMapStyleId;
    let mode = prevMode;
    if (mode === 'outdoors' || mode === 'terrain') mode = 'hybrid';
    let resolved = resolveBasemapIdForStyleMode(seedId, mode, settings);
    let nextMode = mode;
    if (!resolved.basemapId) {
      const first = resolveFirstAvailableStyleMode(seedId, settings);
      if (!first.basemapId) {
        message.warning(resolved.hint || '该图源当前不可用');
        return;
      }
      resolved = first;
      nextMode = first.styleMode;
    }
    if (nextMode !== prevMode) {
      setCurrentMapStyleId(nextMode);
      resetTerrainForStyleMode(nextMode);
    }
    const nextOverlays = syncOverlaysForStyleMode(
      resolved.basemapId,
      nextMode,
      activeOverlayIdsRef.current,
      settings
    );
    activeOverlayIdsRef.current = nextOverlays;
    setActiveOverlayIds(nextOverlays);
    writeLastOverlayChoice(nextOverlays);
    switchBasemap(resolved.basemapId, { overlayIds: nextOverlays, styleMode: nextMode });
  };

  const enabledBasemaps = getEnabledProviders(mapProviderSettings, 'basemap');
  const enabledOverlays = getEnabledProviders(mapProviderSettings, 'overlay');

  const renderBasemapLayerControl = (compact = false) => (
    <BasemapLayerControl
      compact={compact}
      showPanel={showBasemapPanel}
      onTogglePanel={() => setShowBasemapPanel((open) => !open)}
      enabledBasemaps={enabledBasemaps}
      enabledOverlays={enabledOverlays}
      mapProviderSettings={mapProviderSettings}
      currentBasemapId={currentBasemapId}
      activeOverlayIds={activeOverlayIds}
      onSwitchBasemap={(id) => {
        switchBasemapKeepingMode(id);
        if (compact) setShowBasemapPanel(false);
      }}
      onToggleOverlay={toggleOverlay}
      terrain3dOn={terrain3dOn}
      onToggleTerrain3d={toggleTerrain3d}
    />
  );

  return {
    currentMapStyleId,
    setCurrentMapStyleId,
    styleModeAvailability,
    switchBasemap,
    toggleOverlay,
    switchMapStyle,
    renderBasemapLayerControl
  };
}
